// Event dates and times are always entered and shown in Belgrade time — the
// events are in Serbia, so a press user abroad or an admin whose laptop is
// set to another zone must still see (and set) the same wall-clock times.
// Timestamps are stored as UTC instants; these helpers convert between that
// and Belgrade calendar date + time, summer/winter time included.
export const BELGRADE_TZ = "Europe/Belgrade";

const partsFormatter = new Intl.DateTimeFormat("en-GB", {
  timeZone: BELGRADE_TZ,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

// { year, month (1-12), day, hours, minutes } in Belgrade, or null.
export const belgradeParts = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date)) return null;
  const parts = Object.fromEntries(
    partsFormatter.formatToParts(date).map((p) => [p.type, p.value]),
  );
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hours: Number(parts.hour),
    minutes: Number(parts.minute),
  };
};

// Belgrade's UTC offset (minutes) at a given instant.
const offsetMinutes = (utcMs) => {
  const p = belgradeParts(new Date(utcMs));
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hours, p.minutes);
  return Math.round((asUtc - Math.floor(utcMs / 60000) * 60000) / 60000);
};

// Belgrade wall-clock date + time -> ISO string (UTC instant).
export const fromBelgrade = (year, month, day, hours = 0, minutes = 0) => {
  const guess = Date.UTC(year, month - 1, day, hours, minutes);
  // Offset depends on the instant itself (DST), so correct once more after
  // the first adjustment.
  let utc = guess - offsetMinutes(guess) * 60000;
  utc = guess - offsetMinutes(utc) * 60000;
  return new Date(utc).toISOString();
};

const pad = (n) => String(n).padStart(2, "0");

export const formatDateBg = (value) => {
  const p = belgradeParts(value);
  return p ? `${pad(p.day)}.${pad(p.month)}.${p.year}` : "";
};

export const formatTimeBg = (value) => {
  const p = belgradeParts(value);
  return p ? `${pad(p.hours)}:${pad(p.minutes)}` : "";
};

export const formatDateTimeBg = (value) => {
  const p = belgradeParts(value);
  return p
    ? `${pad(p.day)}.${pad(p.month)}.${p.year} ${pad(p.hours)}:${pad(p.minutes)}`
    : "";
};

// "02.03.2027 09:00 – 13.03.2027 18:00"; a same-day range keeps one date:
// "02.03.2027 09:00 – 18:00".
export const formatDateTimeRangeBg = (from, to) => {
  if (!from && !to) return "";
  if (!to) return formatDateTimeBg(from);
  if (!from) return formatDateTimeBg(to);
  const a = belgradeParts(from);
  const b = belgradeParts(to);
  const sameDay =
    a && b && a.year === b.year && a.month === b.month && a.day === b.day;
  return sameDay
    ? `${formatDateTimeBg(from)} – ${formatTimeBg(to)}`
    : `${formatDateTimeBg(from)} – ${formatDateTimeBg(to)}`;
};

// Number of Belgrade calendar days an event spans, both ends included
// (02.03 09:00 – 02.03 18:00 is 1 day; 02.03 – 13.03 is 12).
export const eventDaysBg = (from, to) => {
  const a = belgradeParts(from);
  const b = belgradeParts(to);
  if (!a || !b) return "";
  const days =
    (Date.UTC(b.year, b.month - 1, b.day) - Date.UTC(a.year, a.month - 1, a.day)) /
    86400000;
  return Math.max(1, Math.round(days) + 1);
};
